import { useMoodTracking } from "@/lib/useMoodTracking";
import { useNavigationPresentation, tradeHubKeys } from "@/lib/tradeNavigation";
import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

// A page-level tabbed hub that reuses existing full pages under one route.
// `base` is the hub root (e.g. /app/safety-qa); `tabs` is an array of
// { key, label, Icon, roles, flag?, Component }. Tabs are filtered by the
// same role + field-flag rules used by the sidebar nav model.
const visibleFor = (t, user) =>
  t.roles.includes(user.role) && (!t.flag || user.role !== "field" || !!user[t.flag]);

export function HubShell({ base, tabs }) {
  const { tab } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { trades } = useNavigationPresentation();

  const mood = useMoodTracking(tabs.some(t => t.key === "mood"));
  const visible = tabs.filter((t) => visibleFor(t, user) && (t.key !== "mood" || mood.enabled));
  const active = visible.some((t) => t.key === tab) ? tab : visible[0]?.key;

  useEffect(() => {
    if (tab === "mood" && mood.loading) return;
    if (!visible.length) { nav("/app", { replace: true }); return; }
    if (!tab || !visible.some((t) => t.key === tab)) nav(`${base}/${active}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, visible.length, mood.loading]);

  if (!visible.length) return null;
  // Only simplify tab links. Direct permitted advanced tabs still render.
  const keys = trades && tradeHubKeys[base];
  const displayed = keys ? visible.filter(t => keys.includes(t.key) || t.key === active) : visible;
  const Active = visible.find((t) => t.key === active)?.Component;
  const hubId = base.split("/").pop();

  return (
    <div data-testid={`hub-${hubId}`}>
      <div
        className="sticky top-0 z-20 bg-[#F7F8FA]/95 dark:bg-[#07090D]/95 backdrop-blur border-b border-zinc-200 dark:border-white/10 px-4 lg:px-6"
        data-testid={`hub-nav-${hubId}`}
      >
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2.5">
          {displayed.map((t) => {
            const on = t.key === active;
            return (
              <Link
                key={t.key}
                to={`${base}/${t.key}`}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-colors ${
                  on
                    ? "bg-[#09090B] dark:bg-[#FF5F15] text-white"
                    : "text-zinc-500 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-white/5"
                }`}
                data-testid={`hub-tab-${t.key}`}
                aria-current={on ? "page" : undefined}
              >
                <t.Icon className="w-3.5 h-3.5" /> {t.label}
              </Link>
            );
          })}
        </div>
      </div>
      <div className="min-w-0" data-testid={`hub-panel-${active}`}>
        {Active && <Active />}
      </div>
    </div>
  );
}
